import { Analytics } from '@cobliteam/analytics/src/Analytics';
import useFeatureFlags, { type FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import { Alert } from '@cobliteam/ui/components';
import Button from '@cobliteam/ui/components/Button';
import { useCallback, useState } from 'react';
import ShineBorder from '../border/ShineBorder';

type ToggleFeatureFlagProps = {
  labelEnabled: string;
  labelDisabled: string;
  labelEnabledTooltip?: string;
  labelDisabledTooltip?: string;
  iconEnabled: React.ReactNode;
  iconDisabled: React.ReactNode;
  flagKey: keyof FeatureFlags;
  className?: string;
  onChangeValue?: (value: boolean) => void;
};

const ToggleFeatureFlag = ({
  flagKey,
  labelEnabled = 'Ativado',
  labelDisabled = 'Desativado',
  labelEnabledTooltip,
  labelDisabledTooltip,
  iconEnabled,
  iconDisabled,
  className,
  onChangeValue,
  ...props
}: ToggleFeatureFlagProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, unregister, [flagKey]: selectedFlag, toggleFeatureFlag } = useFeatureFlags();
  const isEnabled = selectedFlag === true;

  const handleToggle = useCallback(
    async (pressed: boolean) => {
      setIsLoading(true);
      if (pressed) {
        await register(flagKey);
        Analytics.track(`FeatureFlag Enabled - ${flagKey}`);
        Alert.success(labelEnabledTooltip || 'Você ativou a funcionalidade');
      } else {
        await unregister(flagKey);
        Analytics.track(`FeatureFlag Disabled - ${flagKey}`);
        Alert.success(labelDisabledTooltip || 'Você desativou a funcionalidade');
      }
      setIsLoading(false);
    },
    [flagKey, labelEnabledTooltip, labelDisabledTooltip, register, unregister]
  );

  const handleClick = useCallback(() => {
    if (onChangeValue) {
      onChangeValue(!isEnabled);
    }
    handleToggle(!isEnabled);
  }, [handleToggle, isEnabled, onChangeValue]);

  if (!toggleFeatureFlag) return null;

  return (
    <div className='relative rounded-md'>
      {!isEnabled && <ShineBorder shineColor={['transparent', 'transparent', '#A07CFE', '#FE8FB5', '#FFBE7B', 'transparent', 'transparent']} borderWidth={2.5} />}
      <Button
        startIcon={isEnabled ? iconEnabled : iconDisabled}
        data-testid={`toggle-feature-flag-${flagKey}`}
        loading={isLoading}
        size='sm'
        color='default'
        variant='outlined'
        onClick={handleClick}
        className={className}
        {...props}
      >
        {isEnabled ? labelEnabled : labelDisabled}
      </Button>
    </div>
  );
};

export default ToggleFeatureFlag;
